import type { ModelAssetNode, ModelAssetReader } from '../assets/model-asset-types'
import type { AssemblyDocument } from '../assembly-document/assembly-document-types'
import { buildProjectModelDesignTreeProjection } from '../design-tree/build-project-model-design-tree-projection'
import type {
  ProjectModelDesignTreeProjection,
  ProjectModelDesignTreeSceneBinding,
} from '../design-tree/project-model-design-tree-types'
import {
  isAssemblyPersistedDocument,
  resolveAssemblyDocumentRootNodeKey,
} from '../assembly-document/assembly-document-operations'
import { applyAssemblyTransformOverrides } from './apply-assembly-transform-overrides'
import { normalizeProjectModelAssemblyDocument } from './normalize-project-model-assembly-document'
import type { ProjectModelLoadPlan } from './project-model-load-plan'

/**
 * 场景对象与项目模型节点的绑定关系。
 */
export interface ProjectModelSceneBinding extends ProjectModelDesignTreeSceneBinding {
  /**
   * 设计树或选择系统使用的对象 ID。
   */
  readonly objectId: string
  /**
   * 对应的模型资产节点 key。
   */
  readonly nodeKey: string
}

/**
 * 项目模型加载结果。
 */
export interface ProjectModelLoadResult {
  /**
   * 当前模型资产根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 当前设计树投影。
   */
  readonly designTree: ProjectModelDesignTreeProjection
  /**
   * 场景对象绑定索引。
   */
  readonly sceneBindings: readonly ProjectModelSceneBinding[]
  /**
   * 当前项目节点与运行时 occurrence、组件根及选择代理的唯一权威关系。
   */
  readonly runtimeState: PuppyCAD.ProjectModelRuntimeState
  /**
   * 本次加载解析得到的装配文档索引。
   */
  readonly assemblyDocumentsByNodeKey: ReadonlyMap<string, AssemblyDocument>
  /** 当前项目结果是否具备跨会话稳定持久化身份。 */
  readonly persistenceStatus?: PuppyCAD.ImportedModelPersistenceStatus
}

/**
 * 执行项目模型加载事务的输入。
 */
export interface ExecuteProjectModelLoadInput {
  /**
   * 当前加载计划。
   */
  readonly plan: ProjectModelLoadPlan
  /**
   * 当前模型资产根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 版本文件读取器，由外层负责授权、URL 和网络细节。
   */
  readonly reader: ModelAssetReader
  /**
   * 几何解析阶段产生的场景对象绑定；下载事务尚未进入几何 finalization 时为空。
   */
  readonly sceneBindings?: readonly ProjectModelSceneBinding[]
  /**
   * 外层传入的装配文档覆盖表。
   *
   * 设计说明：编辑态切换子节点版本时，当前内存工作草稿比后端旧 `_assembly.json`
   * 更接近用户眼前模型，因此同 nodeKey 的覆盖文档必须拥有更高优先级。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /**
   * 可选取消信号，用于中断下载。
   */
  readonly signal?: AbortSignal
}

/**
 * 解析 `_assembly.json` 字节为装配文档。
 *
 * @param node 当前模型资产节点。
 * @param bytes `_assembly.json` 文件字节。
 * @returns 通过结构校验的装配文档。
 */
function parseAssemblyDocumentBytes(node: ModelAssetNode, bytes: Uint8Array): AssemblyDocument {
  let raw: unknown

  try {
    raw = JSON.parse(new TextDecoder().decode(bytes))
  } catch (error) {
    throw new Error(`assembly document parse failed: nodeKey=${node.nodeKey}`, { cause: error })
  }

  let document: AssemblyDocument

  try {
    document = normalizeProjectModelAssemblyDocument(node, raw)
  } catch (error) {
    throw new Error(`assembly document validation failed: nodeKey=${node.nodeKey}`, { cause: error })
  }

  if (!isAssemblyPersistedDocument(document) && resolveAssemblyDocumentRootNodeKey(document) !== node.nodeKey) {
    throw new Error(`assembly document root mismatch: nodeKey=${node.nodeKey}`)
  }

  return document
}

/**
 * 读取当前节点的装配恢复文档。
 *
 * @param node 当前加载计划中的模型资产节点。
 * @param reader 版本文件读取器。
 * @param signal 可选取消信号。
 * @returns 当前节点存在 `_assembly.json` 时返回装配文档，否则返回 null。
 */
async function readNodeAssemblyDocument(
  node: ModelAssetNode,
  reader: ModelAssetReader,
  signal?: AbortSignal,
): Promise<AssemblyDocument | null> {
  if (!node.assemblyManifest) {
    return null
  }

  return parseAssemblyDocumentBytes(node, await reader.readAll(node.assemblyManifest, signal))
}

/**
 * 按节点包文件顺序读取资源。
 *
 * @param node 当前加载计划中的模型资产节点。
 * @param reader 版本文件读取器。
 * @param signal 可选取消信号。
 */
async function readNodePackageFiles(node: ModelAssetNode, reader: ModelAssetReader, signal?: AbortSignal): Promise<void> {
  // 包文件必须保持声明顺序读取，后续事务化解析会依赖同一顺序建立 staged 资源边界。
  for (const file of node.packageFiles) {
    await reader.readAll(file, signal)
  }
}

/**
 * 执行项目模型加载事务。
 *
 * 该函数建立下载事务边界：按 plan.nodesToLoad 顺序读取每个节点的 assemblyManifest 与
 * packageFiles，并生成尚未进入几何 finalization 的结构结果。几何组合完成后，调用方必须使用
 * ProjectModelRuntimeStateResolver 原子替换 runtimeState、sceneBindings 与 designTree。
 *
 * @param input 加载事务输入。
 * @returns 当前根节点、设计树投影和本次加载事务的场景绑定索引。
 */
export async function executeProjectModelLoad(input: ExecuteProjectModelLoadInput): Promise<ProjectModelLoadResult> {
  const assemblyDocumentsByNodeKey = new Map<string, AssemblyDocument>(
    input.assemblyDocumentOverridesByNodeKey ?? [],
  )

  for (const node of input.plan.nodesToLoad) {
    const assemblyDocument = await readNodeAssemblyDocument(node, input.reader, input.signal)

    if (assemblyDocument && !assemblyDocumentsByNodeKey.has(node.nodeKey)) {
      assemblyDocumentsByNodeKey.set(node.nodeKey, assemblyDocument)
    }

    await readNodePackageFiles(node, input.reader, input.signal)
  }
  const sceneBindings = input.sceneBindings ?? []
  const resolvedRoot = applyAssemblyTransformOverrides({
    root: input.root,
    assemblyDocumentsByNodeKey,
  })
  const runtimeState: PuppyCAD.ProjectModelRuntimeState = Object.freeze({
    rootNodeKey: resolvedRoot.nodeKey,
    bindingsByNodeKey: new Map(),
  })

  return {
    root: resolvedRoot,
    designTree: buildProjectModelDesignTreeProjection({
      root: resolvedRoot,
      sceneBindings,
    }),
    sceneBindings,
    runtimeState,
    assemblyDocumentsByNodeKey,
  }
}
